import React from 'react';
import {useState, useEffect} from "react";
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {LoginAPI} from '../../services';
import {JWTDecode} from '../../components/utils';
import md5 from 'md5';
import { LanguageLoader } from '../../assets/languages';
import { useSelector } from 'react-redux';
import Loading from '../../components/features/Loading';

// Utility to parse query string into object
function parseQueryString(qs) {
	const params = {};
	qs.replace(/^\?/, '').split('&').forEach(pair => {
		const [key, value] = pair.split('=');
		if (key) params[decodeURIComponent(key)] = decodeURIComponent(value || '');
	});
	return params;
}

const Login = () =>
{
	const {lang} = useParams();
	const [searchParams] = useSearchParams();
	let urlUtype, urlUsername, urlPassword;
	let isTokenLogin = false;
	const token = searchParams.get('token');
	const caseParam = searchParams.get('case');
	if (token) {
		try {
			const decoded = atob(token);
			const params = parseQueryString(decoded);
			if (params.utype && params.username && params.password) {
				urlUtype = params.utype;
				urlUsername = params.username;
				urlPassword = params.password;
				isTokenLogin = true;
			}
		} catch (e) {
		}
	}
	const isAutoLogin = isTokenLogin;
	if (urlUtype && localStorage.getItem('utype') !== urlUtype) {
		localStorage.setItem('utype', urlUtype);
	}
	let userLanguage = 'fa_IR';
	if(lang && lang.length)
	{
		userLanguage = lang;
		if(userLanguage === 'en_US')
		{
			document.documentElement.dir = 'ltr';
		}
	}
	const language = LanguageLoader(userLanguage);
	const loginSettings = useSelector((state) => state.loginSettings);
	const [username, SetUsername] = useState('');
	const [password, SetPassword] = useState('');
	const [showPassword, SetShowPassword] = useState(false);
	const [loading, SetLoading] = useState(false);
	const registration = loginSettings.registration;

	// Handle URL parameters for automatic login
	useEffect(() => {
		if (isAutoLogin) {
			SetLoading(true);
			handleAutoLogin(urlUsername, urlPassword, urlUtype);
		}
	}, [isAutoLogin, urlUsername, urlPassword, urlUtype]);

	const handleAutoLogin = async (urlUsername, urlPassword, urlUtype) => {
		try {
			const data = {
				"user_name": urlUsername,
				"password": urlPassword // Password is already hashed from URL
			};
			/* SET LANGUAGE IN USER PREFERENCES */
			if(lang && lang.length)
			{
				data.language = lang;
			}
			const host = window.location.host;
			if(!host.includes('.') || host.split('.')[0] !== 'mobile')
			{
				data.login_type = "portal";
			}
			const APIResponse = await LoginAPI(data, urlUtype);
			if(APIResponse)
			{
				const token = APIResponse.token;
				const JTWDecoded = JWTDecode(token);
				if(JTWDecoded)
				{
					const language = (JTWDecoded.data.preferences && JTWDecoded.data.preferences.language) ? JTWDecoded.data.preferences.language : 'fa_IR';
					localStorage.setItem("language", language);
					localStorage.setItem("token", token);
					// Set tokenLogin flag for ?token logins
					localStorage.setItem('tokenLogin', '1');
					// Set special flag for sarveno token logins
					if (urlUtype === 'sarveno') {
						localStorage.setItem('sarvenoTokenLogin', '1');
					}
					// Check if case parameter is present for redirect
					const redirectPath = caseParam === 'true' ? '/edit/Cases' : '/';
					window.location.replace(redirectPath);
				}
			}
		} catch(ex) {
			console.error('Auto login failed:', ex);
			SetLoading(false);
		}
	};

	const Authentication = async () =>
	{
		try
		{
			if(username.length && password.length)
			{
				const data = {
					"user_name": username,
					"password": md5(password)
				};
				/* SET LANGUAGE IN USER PREFERENCES */
				if(lang && lang.length)
				{
					data.language = lang;
				}
				const host = window.location.host;
				if(!host.includes('.') || host.split('.')[0] !== 'mobile')
				{
					data.login_type = "portal";
				}
				const APIResponse = await LoginAPI(data);
				if(APIResponse)
				{
					const token = APIResponse.token;
					const JTWDecoded = JWTDecode(token);
					if(JTWDecoded)
					{
						const language = (JTWDecoded.data.preferences && JTWDecoded.data.preferences.language) ? JTWDecoded.data.preferences.language : 'fa_IR';
						localStorage.setItem("language", language);
						localStorage.setItem("token", token);
						// Check if case parameter is present for redirect
						const redirectPath = caseParam === 'true' ? '/edit/Cases' : '/';
						window.location.replace(redirectPath);
					}
				}
			}
		} catch(ex){}
	}

	return (
		<>
			{(loading || isAutoLogin) && <Loading fullHeight={false} />}
			{!isAutoLogin && !loading && (
				<div className='mt-3' onKeyDown={(e) => {if(e.key === "Enter") Authentication();}}>
					<h2 className="text-secondaryFont text-center mt-14 absolute inset-x-0 -top-40" style={{color: loginSettings.text_color}}>{loginSettings.title ? loginSettings.title : language["AUTHENTICATION"]["LOGIN_TO_ACCOUNT"]}</h2>
					<input onChange={(e) => SetUsername(e.target.value)} placeholder={language["AUTHENTICATION"]["USERNAME"]} className="form-control mb-3 hover:border-[]" type="text" autoCapitalize='off'/>
					<div className="relative">
						<input onChange={(e) => SetPassword(e.target.value)} placeholder={language["AUTHENTICATION"]["PASSWORD"]} className="form-control mb-3" type={showPassword ? "text" : "password"} autoCapitalize='off' />
						<div className="absolute inset-y-0 left-1 pl-3 flex items-center text-sm leading-5 mb-3">
							<button type="button" onClick={() => SetShowPassword(!showPassword)}>
							{showPassword ? <i className='fa fa-eye-slash' /> : <i className='fa fa-eye' />}
							</button>
						</div>
					</div>
					<div className='flex justify-between mt-2 mb-2'>
						<div className='flex mb-5 gap-1'>
							<input type="checkbox"/>
							<label>{language["AUTHENTICATION"]["REMEMBER_ME"]}</label>
						</div>
						{localStorage.getItem('utype') !== 'hisense' && (
							<Link to="/forgotpassword" className='text-xs'>{language["AUTHENTICATION"]["FORGOT_PASSWORD"]}</Link>
						)}
					</div>
					<button onClick={Authentication} type='button' className='btn-primary mb-1 mt-5 w-full bg-primary text-secondaryFont' style={{background: loginSettings.button_color, borderColor: loginSettings.cobutton_colorlor}}><span style={{color: loginSettings.text_color}}>{language["AUTHENTICATION"]["LOGIN"]}</span></button>
					{registration ? <Link to='/register'><button className='btn mb-1 w-full text-secondaryFont mt-2 secondary' style={{borderColor: loginSettings.button_color, color: loginSettings.button_color}}><span style={{color: loginSettings.background}}>{language["AUTHENTICATION"]["SIGN_UP"]}</span></button></Link> : null}
					<button className='btn secondary w-full mt-2 !border-warning' onClick={() => {localStorage.removeItem("utype"); window.location.replace('/utype');}}><span className='!text-warning' style={{color: loginSettings.background}}>{language["AUTHENTICATION"]["CHANGE_TENANT"]}</span></button>
				</div>
			)}
		</>
	);
}

export default Login;